import React from "react";
import styled from "styled-components";
import { DownloadSimple, ShareNetwork } from "@phosphor-icons/react";

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  DEFAULT_EGG_ID,
  FRAME_OUTER_HEIGHT,
  FRAME_OUTER_WIDTH,
  FRAME_SLICE_LEFT,
  FRAME_SLICE_TOP,
} from "../../constants";
import { downloadComposite } from "../../composite";
import { eggImageUrl, marcImageUrl } from "../../paths";
import { eggTransformToCss } from "../../virtualCoords";
import ActionButton from "../ActionButton";
import Button from "../Button";
import FitContainer from "../FitContainer";
import _Modal, { ModalTitle } from "../Modal";

const Modal = styled(_Modal)`
  background-image:
    linear-gradient(to right, var(--pattern-color) 1px, transparent 1px),
    linear-gradient(to bottom, var(--pattern-color) 1px, transparent 1px);
  background-size: var(--pattern-size) var(--pattern-size);
`;

// Round-complete recap.
//
// One row, all three drawings the same size. Each drawing wears a
// big circular marc-photo "tag" pinned to its upper-right corner —
// since the egg is always positioned toward the bottom of the
// frame in play, the upper-right of every drawing tile is mostly
// empty orange canvas-bg, which gives us plenty of room for a
// reference circle without obscuring the actual artwork.
//
// Sort order is by per-marc duration (fastest first). No
// click-to-promote — equal-sized tiles convey "all three are the
// recap" without needing a winner.
//
// Drawings render from `drawingSrc` (data URL captured at
// triggerShowcase). Strokes/palette/eggTransform/marcTransform/
// finishedAt/duration ride along in the same snapshot;
// marcTransform is kept around for a possible future rectangular-
// frame variant of the marc tag.
function EndScreenModal({
  open,
  onOpenChange,
  lineup,
  roundDrawings,
  elapsedMs,
  onSyndicate,
}) {
  const orderedByDuration = React.useMemo(() => {
    return [...lineup].sort((a, b) => {
      const da = roundDrawings[a]?.duration ?? Infinity;
      const db = roundDrawings[b]?.duration ?? Infinity;
      return da - db;
    });
  }, [lineup, roundDrawings]);

  return (
    <Modal open={open} onOpenChange={onOpenChange} width={820}>
      <Stack>
        <Header>
          <Eyebrow>Velocity established</Eyebrow>
          <ModalTitle>
            You shipped in: <Time>{formatElapsed(elapsedMs)}</Time>
          </ModalTitle>
        </Header>

        <Row>
          {orderedByDuration.map((id) => (
            <MarcSlot
              key={id}
              marcId={id}
              snapshot={roundDrawings[id]}
              onSyndicate={onSyndicate}
            />
          ))}
        </Row>

        <Footer>
          <Button
            variant="primary"
            size="large"
            onClick={() => onOpenChange(false)}
          >
            Keep Building
          </Button>
        </Footer>
      </Stack>
    </Modal>
  );
}

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

// One slot = the framed drawing + its marc-photo tag in the upper
// right. The tag is sized as a fraction of the slot width
// (--marc-tag-size) so it scales with the column when the row
// wraps on narrow viewports.
function MarcSlot({ marcId, snapshot, onSyndicate }) {
  const hasDrawing = !!(snapshot?.strokes && snapshot?.palette);
  const canSyndicate = hasDrawing && !!onSyndicate;
  return (
    <SlotShell>
      <DrawingFrame snapshot={snapshot} />
      <MarcCircle src={marcImageUrl(marcId)} alt="" draggable={false} />
      {hasDrawing && (
        <SlotActions>
          <ActionButton
            type="button"
            disabled={!canSyndicate}
            onClick={() => onSyndicate?.(marcId, snapshot)}
            title="Submit your egg to the gallery and grab share images"
            style={{ minWidth: "128px" }}
          >
            <ShareNetwork size={14} weight="fill" />
            <span>Syndicate</span>
          </ActionButton>
          <ActionButton
            type="button"
            style={{ minWidth: "128px" }}
            onClick={() =>
              downloadComposite({
                marcId,
                eggId: DEFAULT_EGG_ID,
                strokes: snapshot.strokes,
                palette: snapshot.palette,
                eggTransform: snapshot.eggTransform,
                marcTransform: snapshot.marcTransform,
                lights: snapshot.eggLights,
                warmth: snapshot.eggWarmth,
                redshift: snapshot.eggRedshift,
              })
            }
            title="Download a PNG of this Marc"
          >
            <DownloadSimple size={14} weight="bold" />
            <span>Download</span>
          </ActionButton>
        </SlotActions>
      )}
    </SlotShell>
  );
}

// Gold-framed drawing tile. Same FRAME_OUTER virtual stage that the
// play surface and MarcPickerModal use — FitContainer scales it to
// whatever size the parent column gives it.
function DrawingFrame({ snapshot }) {
  const transform = snapshot?.eggTransform;
  const drawingSrc = snapshot?.drawingSrc ?? null;
  return (
    <FrameStage>
      <FitContainer width={FRAME_OUTER_WIDTH} height={FRAME_OUTER_HEIGHT}>
        <FrameInterior>
          <EggImg
            src={eggImageUrl(DEFAULT_EGG_ID)}
            alt=""
            draggable={false}
            style={{ transform: eggTransformToCss(transform) }}
          />
          {drawingSrc && (
            <DrawingImg src={drawingSrc} alt="" draggable={false} />
          )}
        </FrameInterior>
        <FrameOverlay src="/frame.png" alt="" draggable={false} />
      </FitContainer>
    </FrameStage>
  );
}

function formatElapsed(ms) {
  const total = Math.max(0, ms ?? 0);
  const totalTenths = Math.floor(total / 100);
  const tenths = totalTenths % 10;
  const totalSeconds = Math.floor(totalTenths / 10);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60);
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return `${mm}:${ss}.${tenths}`;
}

const Header = styled.div`
  text-align: center;
  margin-bottom: 1rem;

  & > h2 {
    line-height: 1.05;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const Time = styled.span`
  /* font-family: "Fraunces", serif; */
  /* font-size: clamp(2.5rem, 6vw, 3.5rem); */
  /* font-weight: 600; */
  /* letter-spacing: -0.02em; */
  /* line-height: 1; */
  color: var(--color-orange-700);
  /* text-shadow:
    0 1px 0 rgba(255, 255, 255, 0.4),
    0 2px 0 var(--color-text); */
`;

// All three drawings, equal-width. minmax(200px, 1fr) wraps the
// row when the modal is too narrow (mobile / very small windows)
// instead of squeezing the columns past readability.
const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;

  @media (max-width: 640px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

// SlotShell is the click target / position anchor for the marc
// circle. position: relative so MarcCircle's absolute positioning
// is relative to this slot, not the modal.
const SlotShell = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const SlotActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.6rem;
`;

// Marc-photo tag — a much bigger circle than the previous flanker
// thumb, since it now lives in the empty upper portion of every
// drawing (the egg sits at the bottom of every play frame). Sized
// as a fraction of the slot width via container query units so it
// scales with the column when the row wraps.
//
// Slight upper/right overhang reads as a sticker pinned ON the
// frame rather than tucked inside it.
const MarcCircle = styled.img`
  position: absolute;
  top: -25px;
  right: -10px;
  /* ~50% of the slot width — at modal width 820 with 3 columns
     each slot is ~250px, so the circle ends up ~125px. Bigger on
     wider viewports, smaller when the row wraps narrow. */
  width: 45%;
  aspect-ratio: 1;
  border-radius: 50%;
  object-fit: cover;
  background: var(--canvas-bg);
  border: 3px solid var(--color-orange-700);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  user-select: none;
  pointer-events: none;
  z-index: 2;
`;

// Drawing tile chrome (gold frame). Aspect-ratio matches the
// FRAME_OUTER PNG so FitContainer fills it 1:1.
const FrameStage = styled.div`
  width: 100%;
  aspect-ratio: ${FRAME_OUTER_WIDTH} / ${FRAME_OUTER_HEIGHT};
  position: relative;
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.22));
`;

const FrameInterior = styled.div`
  position: absolute;
  top: ${FRAME_SLICE_TOP}px;
  left: ${FRAME_SLICE_LEFT}px;
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
  background: var(--canvas-bg);
  overflow: hidden;
`;

const FrameOverlay = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: ${FRAME_OUTER_WIDTH}px;
  height: ${FRAME_OUTER_HEIGHT}px;
  pointer-events: none;
  user-select: none;
`;

const EggImg = styled.img`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: center center;
  user-select: none;
  pointer-events: none;
`;

const DrawingImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

export default EndScreenModal;
